import type { ReactNode } from "react";
import React, { createContext, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDealFinderTickers } from "hooks/useDealFinderTickers";
import type { DealFinderTicker } from "api/tickers/models/DealFinderTicker";
import type { RootState } from "state/rootReducer";
import { actions as dealFinderActions } from "state/dealFinder/reducer";

interface DealFinderContextType {
  defaultDeals: DealFinderTicker[];
  isDefaultDealsLoaded: boolean;
  isLoadingDefaultDeals: boolean;
}

const DealFinderContext = createContext<DealFinderContextType>({
  defaultDeals: [],
  isDefaultDealsLoaded: false,
  isLoadingDefaultDeals: false,
});

export const useDealFinderContext = () => useContext(DealFinderContext);

interface DealFinderProviderProps {
  children: ReactNode;
}

export const DealFinderProvider: React.FC<DealFinderProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const { defaultDeals, isDefaultDealsLoaded, isLoadingDefaultDeals } = useSelector((state: RootState) => state.dealFinder);
  const isLoggedIn = useSelector((state: RootState) => state.session.isLoggedIn);
  const { getDealFinderTickers } = useDealFinderTickers();

  // Load/refresh default deals when the user logs in
  useEffect(() => {
    const loadDefaultDeals = async () => {
      if (isLoggedIn) {
        // If we don't have deals loaded yet, set loading state
        if (!isDefaultDealsLoaded && !isLoadingDefaultDeals) {
          dispatch(dealFinderActions.setLoadingDefaultDeals(true));
        }
        try {
          // Always fetch fresh data when user logs in (either initial load or refresh)
          const tickers = await getDealFinderTickers({});
          dispatch(dealFinderActions.setDefaultDeals(tickers));
        } catch (error) {
          console.error("Error loading default deals:", error);
          // Only set error state if we don't have existing deals to fall back on
          if (!isDefaultDealsLoaded) {
            dispatch(dealFinderActions.setDefaultDealsLoadError());
          }
        }
      }
    };

    loadDefaultDeals();
  }, [dispatch, getDealFinderTickers, isDefaultDealsLoaded, isLoadingDefaultDeals, isLoggedIn]);

  return (
    <DealFinderContext.Provider value={{ defaultDeals, isDefaultDealsLoaded, isLoadingDefaultDeals }}>
      {children}
    </DealFinderContext.Provider>
  );
};
